/* ═══════════════════════════════════════════════════════════════════════════
   Chase N Chew — sign-in dialog behaviour
   Build stamp: 2026-09-05

   Two jobs in one file, because they are two halves of one handshake:

     1. In the STORE window — intercept every "Login" link, open the dialog
        over the page instead of navigating, and hand off to Shopify's hosted
        sign-in in a small centred popup.

     2. In the POPUP window — recognise that this document is the tail of that
        handoff (it carries `cnc_auth=1`), tell the store window, and close.

   Why a popup rather than an iframe
   ─────────────────────────────────
   The store runs NEW customer accounts. /account/login 302s to shopify.com,
   which is a different origin and sends X-Frame-Options. An iframe of it is a
   permanently empty box. A popup is the only way to keep the store on screen
   while Shopify owns the credentials, and it is the same pattern every OAuth
   sign-in on the web uses.

   Why THREE success signals
   ─────────────────────────
   Auth pages commonly send `Cross-Origin-Opener-Policy: same-origin`, which
   SEVERS window.opener when the popup navigates away. When that happens the
   store window sees `popup.closed === true` while the window is plainly still
   open, and postMessage has nobody to talk to. Treating either as gospel is
   how this breaks on exactly the browsers people use. So:

     · localStorage `storage` event — fires across same-origin windows and does
       not care about opener at all. This is the reliable one.
     · postMessage — instant when opener survived.
     · polling the header section for the customer's signed-in state, plus one
       immediate poll whenever the store window regains focus.

   Any one of them is enough. `popup.closed` is used only as a hint to poll,
   never as proof of anything.

   Vanilla, ES5, no dependencies, safe to load twice.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  if (window.__cncAuthLoaded) return;
  window.__cncAuthLoaded = true;

  var FLAG      = "cnc_auth";          /* marks the popup's landing page       */
  var CHANNEL   = "cnc:auth-done";     /* localStorage key both windows share  */
  var POPUP     = "cncAuthWindow";
  var HTML_OPEN = "cnc-auth-open";
  var POP_W = 460, POP_H = 730;
  var POLL_MS   = 2500;
  var GIVE_UP_MS = 240000;             /* 4 minutes of waiting is a dead flow  */

  window.CNC = window.CNC || {};

  var dlg = null, card = null;
  var popup = null, timer = null, deadline = 0;
  var lastFocus = null, opener = null;
  var loginUrl = "/account/login", returnUrl = "/pages/account";
  var loginHost = "";        /* host of routes.account_login_url, from Liquid */

  /* ═══ THE POPUP HALF ══════════════════════════════════════════════════════
     Runs before anything else and before paint where possible: if this
     document is the popup coming home, the customer should see it vanish, not
     read it. */
  function settleAsPopup() {
    if (window.location.search.indexOf(FLAG + "=1") === -1) return false;

    /* Broadcast first, close second. If close() succeeds the rest of this
       function never runs, and a signal sent after the window is gone is a
       signal nobody hears. */
    try { window.localStorage.setItem(CHANNEL, String(Date.now())); } catch (e) { /* private mode */ }
    try {
      if (window.opener && window.opener !== window) {
        window.opener.postMessage({ cnc: "auth", ok: true }, window.location.origin);
      }
    } catch (e) { /* opener severed by COOP — the storage event covers us */ }

    try { window.close(); } catch (e) { /* refused */ }

    /* Still here? Then the browser would not let us close, and this window IS
       the customer's page now. Strip the flag so a refresh doesn't re-fire the
       handshake, and let the account page render normally. Leaving a dead tab
       that says nothing would be the worse outcome. */
    window.setTimeout(function () {
      try {
        var clean = window.location.pathname + window.location.search
          .replace(new RegExp("[?&]" + FLAG + "=1"), "")
          .replace(/^&/, "?");
        window.history.replaceState({}, "", clean || window.location.pathname);
      } catch (e) { /* old browser */ }
    }, 700);

    return true;
  }

  /* ═══ THE STORE HALF ══════════════════════════════════════════════════════ */

  function header() { return document.querySelector("[data-cnc-header]"); }

  /* Phones don't get a popup. A mobile browser turns window.open into a new
     tab, which is a full context switch with none of the "the store is still
     behind this" benefit and a worse back button. Same-tab is honest there. */
  function isHandheld() {
    return window.matchMedia("(pointer: coarse)").matches && window.innerWidth < 900;
  }

  /* ── Where to send them ───────────────────────────────────────────────────
     `routes.account_login_url` is the WRONG route for this, and the way it is
     wrong is invisible until someone actually signs in.

     It renders the hosted account in full —
     https://shopify.com/<shop_id>/account?locale=…&buyer_flags=<jwt> — and it
     deposits the customer INSIDE customer accounts and leaves them there.
     Measured on the live store: after a successful sign-in the popup sat on
     shopify.com/<shop_id>/account/orders. No return_url on that URL changes
     it; Shopify is behaving as designed. The popup never came home, so it
     never closed, and — the part that actually matters — the storefront
     session was never established on our own domain either.

     `/customer_authentication/login?return_to=…` is the documented route that
     comes BACK to the online store. It is same-origin, so the round trip ends
     on our domain, which is what both sets the session cookie and lets the
     landing page broadcast and close itself.

     Two details that are easy to get wrong and silent when you do:
       · the parameter is `return_to`, NOT `return_url`
       · it takes a RELATIVE path only — an absolute URL is ignored

     The snippet's static hrefs stay on routes.account_login_url so the links
     still work with JS dead; this upgrades them once JS is running. */
  function authRoute() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    if (root.charAt(root.length - 1) !== "/") root += "/";
    return root + "customer_authentication/login";
  }

  function loginTarget(returnPath) {
    return authRoute() + "?return_to=" + encodeURIComponent(returnPath);
  }

  /* Where the popup lands once Shopify is finished. The flag is what the
     popup half recognises as "you are the tail of a handoff, report and go". */
  function popupTarget() {
    return loginTarget(returnUrl + (returnUrl.indexOf("?") === -1 ? "?" : "&") + FLAG + "=1");
  }

  /* Same-tab handoff returns to the page they were reading, not to the account
     page — they clicked Login from somewhere for a reason. */
  function tabTarget() {
    return loginTarget(window.location.pathname + window.location.search);
  }

  function pane(name) {
    if (!dlg) return;
    /* Published on the root as well as toggled per-pane: which glyph owns the
       seam (brand mark, spinner, warning) is a question about the whole card,
       and only the root can answer it in CSS. */
    dlg.setAttribute("data-auth-state", name);
    var panes = dlg.querySelectorAll("[data-auth-pane]");
    Array.prototype.forEach.call(panes, function (p) {
      p.hidden = p.getAttribute("data-auth-pane") !== name;
    });
  }

  /* ── Open / close ──────────────────────────────────────────────────────── */

  function open(fromEl) {
    if (!dlg) return;

    /* Out of the header and onto <body>, once. The header sets a z-index for
       its sticky behaviour, which makes it a stacking context; a
       position:fixed child of one is confined to it and renders UNDER the
       header it was opened from. No z-index on the dialog can win that — only
       leaving the context can. */
    if (dlg.parentNode !== document.body) document.body.appendChild(dlg);

    lastFocus = fromEl || document.activeElement;
    pane("idle");
    dlg.hidden = false;
    document.documentElement.classList.add(HTML_OPEN);
    try { card.focus({ preventScroll: true }); } catch (e) { /* old browser */ }
  }

  function close() {
    if (!dlg || dlg.hidden) return;
    stopWatching();
    dlg.hidden = true;
    document.documentElement.classList.remove(HTML_OPEN);
    if (lastFocus && lastFocus.isConnected) {
      try { lastFocus.focus(); } catch (e) { /* gone */ }
    }
    lastFocus = null;
  }

  /* ── The handoff ───────────────────────────────────────────────────────── */

  function go() {
    if (isHandheld()) { window.location.href = tabTarget(); return; }

    var w = POP_W, h = POP_H;
    /* Centre on the window the customer is looking at, not on the primary
       display — a second monitor otherwise gets a dialog on one screen and
       its sign-in window on another. */
    var dx = window.screenLeft != null ? window.screenLeft : (window.screenX || 0);
    var dy = window.screenTop != null ? window.screenTop : (window.screenY || 0);
    var vw = window.outerWidth || document.documentElement.clientWidth;
    var vh = window.outerHeight || document.documentElement.clientHeight;
    var left = Math.max(0, Math.round(dx + (vw - w) / 2));
    var top  = Math.max(0, Math.round(dy + (vh - h) / 2.4));

    try {
      popup = window.open(
        popupTarget(), POPUP,
        "popup=yes,width=" + w + ",height=" + h + ",left=" + left + ",top=" + top +
        ",scrollbars=yes,resizable=yes,status=no,toolbar=no,menubar=no"
      );
    } catch (e) { popup = null; }

    if (!popup) { pane("blocked"); return; }

    try { popup.focus(); } catch (e) { /* fine */ }
    pane("wait");
    startWatching();
  }

  /* ── Watching for the sign-in to land ──────────────────────────────────── */

  function startWatching() {
    stopWatching();
    deadline = Date.now() + GIVE_UP_MS;
    timer = window.setInterval(tick, POLL_MS);
    window.addEventListener("storage", onStorage);
    window.addEventListener("message", onMessage);
    window.addEventListener("focus", onFocus);
  }

  function stopWatching() {
    if (timer) { window.clearInterval(timer); timer = null; }
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("message", onMessage);
    window.removeEventListener("focus", onFocus);
    popup = null;
  }

  function onStorage(e) { if (e && e.key === CHANNEL) succeed(); }

  function onMessage(e) {
    if (!e || e.origin !== window.location.origin) return;
    if (e.data && e.data.cnc === "auth" && e.data.ok) succeed();
  }

  /* The customer coming back to this window is the single best moment to ask
     "are they in yet?" — it is exactly when the answer usually changes. */
  function onFocus() { probe(); }

  function tick() {
    if (Date.now() > deadline) { pane("blocked"); stopWatching(); return; }
    probe();
  }

  /* Re-render the header section server-side and read the signed-in flag off
     it. The header is the one section guaranteed to be on every page, and it
     already carries data-cnc-auth-state for exactly this.

     No Accept: application/json header — the Section Rendering API answers
     /cart with the cart object instead of the section when it sees one. */
  function probe() {
    var h = header();
    var id = h && h.getAttribute("data-section-id");
    if (!id || !window.fetch) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    fetch(root + "?sections=" + encodeURIComponent(id) + "&_=" + Date.now(), {
      credentials: "same-origin"
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var html = data && data[id];
        if (typeof html === "string" && html.indexOf('data-cnc-auth-state="in"') !== -1) succeed();
      })
      .catch(function () { /* offline or blocked — the other two signals stand */ });
  }

  function succeed() {
    /* Tidy the popup away before the reload. It normally closes itself the
       moment it lands back on our domain, but this is the safety net for the
       case where it did not — closing an already-closed window is a no-op, and
       `popup.closed` is NOT a usable guard here because COOP makes it report
       true while the window is still on screen. */
    try { if (popup) popup.close(); } catch (e) { /* COOP severed the handle */ }
    stopWatching();
    /* A reload rather than a DOM patch. Signing in changes the header, the
       account links, the review form, the points balance and what the cart
       will do at checkout — every one of them server-rendered. Re-fetching the
       page is the only way to be sure none of them is left showing the guest
       version. */
    window.location.reload();
  }

  /* ── Focus trap ────────────────────────────────────────────────────────── */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trap(e) {
    if (e.key !== "Tab" || !card) return;
    var items = Array.prototype.filter.call(card.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ── Swipe to dismiss (phone) ──────────────────────────────────────────────
     Bound to the grab handle and the crown only, never the whole card. A drag
     listener over the buttons turns every tap that moves two pixels into a
     gesture, and the primary CTA stops working on exactly the devices this is
     for. */
  function bindSwipe() {
    var handles = [dlg.querySelector(".cnc-auth-grab"), dlg.querySelector(".cnc-auth-crown")];
    var y0 = null;

    function down(e) { if (e.pointerType === "mouse") return; y0 = e.clientY; card.style.transition = "none"; }
    function move(e) {
      if (y0 === null) return;
      card.style.transform = "translateY(" + Math.max(0, e.clientY - y0) + "px)";
    }
    function up(e) {
      if (y0 === null) return;
      var dy = Math.max(0, e.clientY - y0);
      y0 = null;
      card.style.transition = "";
      card.style.transform = "";
      if (dy > 90) close();
    }

    handles.forEach(function (h) { if (h) h.addEventListener("pointerdown", down); });
    dlg.addEventListener("pointermove", move);
    dlg.addEventListener("pointerup", up);
    dlg.addEventListener("pointercancel", up);
  }

  /* ── Wiring ────────────────────────────────────────────────────────────── */

  function bind(node) {
    dlg = node;
    card = dlg.querySelector("[data-auth-card]");
    loginUrl = dlg.getAttribute("data-auth-login") || loginUrl;
    returnUrl = dlg.getAttribute("data-auth-return") || returnUrl;
    try {
      var lu = new URL(loginUrl, window.location.href);
      if (lu.host !== window.location.host) loginHost = lu.host;
    } catch (e) { /* relative route — the same-origin branch covers it */ }

    /* The blocked pane's anchor is a real link and must keep working with JS
       dead — but while JS is alive it should still come back to this page. */
    var out = dlg.querySelector("[data-auth-fallback]");
    if (out) out.setAttribute("href", tabTarget());

    dlg.addEventListener("click", function (e) {
      if (e.target.closest("[data-auth-close]")) { e.preventDefault(); close(); return; }
      if (e.target.closest("[data-auth-go]"))     { e.preventDefault(); go(); return; }
      if (e.target.closest("[data-auth-reopen]")) { e.preventDefault(); go(); return; }
    });

    dlg.addEventListener("keydown", function (e) {
      if (e.key === "Escape") { e.stopPropagation(); close(); return; }
      trap(e);
    });

    bindSwipe();
  }

  /* ONE delegated listener on the document, rather than a marker attribute on
     every login CTA in the theme. There are four today — the header, the
     account page's guest gate, the reviews empty state, and whatever the host
     theme renders — and the next one somebody adds should be caught without
     anybody remembering this file exists.

     `data-cnc-auth-skip` on an anchor opts back out. */
  /* What a "login link" actually looks like here.

     Three shapes so far, and each one broke the matcher that handled the last:

       1. /account/login                        — classic customer accounts
       2. https://shopify.com/<shop_id>/account — new customer accounts
       3. https://account.chasenchew.in/        — new customer accounts with a
                                                  branded subdomain

     Shape 2 is why matching the substring "/account/login" found nothing on
     the live store. Shape 3 is why matching "/account" in the PATH then found
     nothing either: with a branded customer-accounts subdomain the word moves
     into the HOST and the path collapses to "/", carrying only the locale and
     buyer_flags query. Every Login click fell straight through both times.

     So stop pattern-matching the URL and ask Liquid instead. `data-auth-login`
     on the dialog is `routes.account_login_url` verbatim — whatever host this
     store's customer accounts live on today. Any link to that host IS a login
     link, by definition, and the rule needs no maintenance the next time
     Shopify changes the shape.

     Deliberately NOT "any sibling subdomain of the storefront": that would
     make blog.chasenchew.in a login link too. The host has to be the one
     Liquid actually rendered.

     The shopify.com branch stays as a safety net for links the host theme or
     a cached page still points at the old hosted URL. Excluded throughout:
     /pages/* (the CNC account page contains the word "account") and anchors
     inside the dialog (its fallback link is a real navigation and must not
     re-open the dialog it lives in). */
  function isLoginLink(a) {
    if (a.closest("[data-cnc-auth-dialog]")) return false;

    var url;
    try { url = new URL(a.href, window.location.href); } catch (e) { return false; }

    var path = url.pathname;
    if (path.indexOf("/pages/") === 0) return false;

    if (url.host === window.location.host) {
      return /\/account\/(login|register)\b/.test(path);
    }
    if (loginHost && url.host === loginHost) return true;
    return /(^|\.)shopify\.com$/.test(url.host) && /\/(authentication|account)(\/|$)/.test(path);
  }

  function onDocClick(e) {
    if (!dlg) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var trigger = e.target.closest("[data-cnc-auth-open]");
    if (!trigger) {
      var a = e.target.closest("a[href]");
      if (!a || a.hasAttribute("data-cnc-auth-skip")) return;
      if (a.getAttribute("target") === "_blank") return;
      if (!isLoginLink(a)) return;
      trigger = a;
    }

    e.preventDefault();
    open(trigger);
  }

  window.CNC.openAuth = function (fromEl) { open(fromEl || null); };

  function boot() {
    var nodes = document.querySelectorAll("[data-cnc-auth-dialog]");
    if (!nodes.length) return;

    /* The theme editor re-renders the header on every setting change, which
       prints a second copy of a dialog whose first copy now lives on <body>.
       Keep the bound one; drop the fresh duplicate. */
    if (dlg && dlg.isConnected) {
      Array.prototype.forEach.call(nodes, function (n) {
        if (n !== dlg && n.parentNode) n.parentNode.removeChild(n);
      });
      return;
    }
    bind(nodes[0]);
  }

  if (settleAsPopup()) return;

  document.addEventListener("click", onDocClick, true);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  document.addEventListener("shopify:section:load", boot);
})();
